import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { PersonaCard } from "./PersonaCard";
import { PersonaEditorModal } from "./PersonaEditorModal";
import { PersonaViewModal } from "./PersonaViewModal";
import { 
  Plus, 
  Copy, 
  Search, 
  Sparkles, 
  User, 
  Users, 
  Building2,
  X
} from "lucide-react";
import { 
  fetchPersonas, 
  createPersona, 
  duplicatePersona 
} from "@/lib/persona-api";
import type { PersonaWithPermissions, PersonaScope, PersonaFormData } from "@/lib/persona-types";
import { useToast } from "@/hooks/use-toast";

interface PersonaPickerProps {
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  showOrgTab?: boolean;
}

export function PersonaPicker({
  selectedIds,
  onSelectionChange,
  showOrgTab = true,
}: PersonaPickerProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<PersonaScope>("recommended");
  const [personas, setPersonas] = useState<PersonaWithPermissions[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal states
  const [editorOpen, setEditorOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<PersonaWithPermissions | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadPersonas = async (scope: PersonaScope) => {
    setIsLoading(true);
    try {
      const data = await fetchPersonas(scope);
      setPersonas(data);
    } catch (error) {
      console.error("Failed to load personas:", error);
      toast({
        title: "Error",
        description: "Failed to load personas",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPersonas(activeTab);
  }, [activeTab]);

  const handleTabChange = (value: string) => {
    setActiveTab(value as PersonaScope);
    setSearchQuery("");
  };

  const handleSelect = (persona: PersonaWithPermissions) => {
    if (selectedIds.includes(persona.id)) {
      onSelectionChange(selectedIds.filter(id => id !== persona.id));
    } else {
      onSelectionChange([...selectedIds, persona.id]);
    }
  };

  const handleView = (persona: PersonaWithPermissions) => {
    setSelectedPersona(persona);
    setViewModalOpen(true);
  };

  const handleDuplicate = async (persona: PersonaWithPermissions) => {
    try {
      await duplicatePersona(persona.id);
      toast({
        title: "Success",
        description: `Duplicated "${persona.name}" to your personas`,
      });
      if (activeTab === "mine") {
        loadPersonas("mine");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to duplicate persona",
        variant: "destructive",
      });
    }
  };

  const handleCreateNew = () => {
    setSelectedPersona(null);
    setEditorOpen(true);
  };

  const handleSavePersona = async (data: PersonaFormData) => {
    setIsSaving(true);
    try {
      await createPersona(data);
      toast({
        title: "Success",
        description: "Persona created successfully",
      });
      setEditorOpen(false);
      if (activeTab === "mine") {
        loadPersonas("mine");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create persona",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredPersonas = personas.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Get selected personas from all tabs for display
  const selectedPersonas = personas.filter(p => selectedIds.includes(p.id));

  return (
    <div className="space-y-4">
      {/* Selected Personas Chips */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg">
          <span className="text-sm text-muted-foreground mr-2 self-center">
            Selected ({selectedIds.length}):
          </span>
          {selectedPersonas.map(persona => (
            <Badge 
              key={persona.id} 
              variant="secondary" 
              className="pl-3 pr-1 py-1 gap-1"
            >
              {persona.name}
              <button
                onClick={() => handleSelect(persona)}
                className="ml-1 hover:text-destructive"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleCreateNew}>
          <Plus className="w-4 h-4 mr-2" />
          Create New
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full justify-start bg-muted/50">
          <TabsTrigger value="recommended" className="gap-2">
            <Sparkles className="w-4 h-4" />
            Recommended
          </TabsTrigger>
          <TabsTrigger value="mine" className="gap-2">
            <User className="w-4 h-4" />
            My Personas
          </TabsTrigger>
          <TabsTrigger value="shared" className="gap-2">
            <Users className="w-4 h-4" />
            Shared
          </TabsTrigger>
          {showOrgTab && (
            <TabsTrigger value="org" className="gap-2">
              <Building2 className="w-4 h-4" />
              Org Library
            </TabsTrigger>
          )}
        </TabsList>

        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search personas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Content */}
        <div className="mt-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-40" />
              ))}
            </div>
          ) : filteredPersonas.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                <User className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">No personas found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {activeTab === "mine" 
                  ? "Create your first persona to get started"
                  : searchQuery 
                    ? "Try a different search term"
                    : "No personas available in this category"
                }
              </p>
              {activeTab === "mine" && (
                <Button onClick={handleCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Persona
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2">
              {filteredPersonas.map(persona => (
                <PersonaCard
                  key={persona.id}
                  persona={persona}
                  isSelected={selectedIds.includes(persona.id)}
                  onSelect={() => handleSelect(persona)}
                  onView={() => handleView(persona)}
                  onDuplicate={() => handleDuplicate(persona)}
                />
              ))}
            </div>
          )}
        </div>
      </Tabs>

      {/* Modals */}
      <PersonaEditorModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        persona={null}
        onSave={handleSavePersona}
        isLoading={isSaving}
      />

      <PersonaViewModal
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
        persona={selectedPersona}
      />
    </div>
  );
}
